import { authResetPassword } from '@/redux/actions';
import * as Constants from '@/redux/constants';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { Alert, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useDialogs } from '@toolpad/core';
import icon from 'assets/login-page-icon.png';
import { Button } from 'components/button';
import { PasswordInput, passwordStrengthCriteria, TextInput } from 'components/input';
import { LanguageSwitcher } from 'components/language-switcher';
import { useDispatch, useLanguageNavigate, useLocale, useSelector } from 'hooks';
import { useI18n } from 'locales';
import get from 'lodash/get';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { shallowEqual } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

const selector = (state) => ({
  authResetPasswordInfo: state.authResetPasswordInfo,
});

function ResetPasswordContent() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const dispatch = useDispatch();
  const dialogs = useDialogs();
  const locale = useLocale();
  const t = useI18n();
  const navigate = useLanguageNavigate();
  const { authResetPasswordInfo } = useSelector(selector, shallowEqual);
  const { formState: { errors }, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      email: email || '',
      token: token || '',
      password: '',
      confirmPassword: '',
    },
  });

  const isFetching = authResetPasswordInfo.isFetching;
  const watchedPassword = watch('password');
  const watchedConfirmPassword = watch('confirmPassword');
  const isPasswordStrong = passwordStrengthCriteria.every((test) => test(watchedPassword || ''));

  useEffect(() => {
    if (!email || !token) {
      navigate('/forgot-password');
    } else {
      setValue('email', email);
      setValue('token', token);
    }
  }, [email, token, setValue, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    switch (authResetPasswordInfo.type) {
      case Constants.AUTH_RESET_PASSWORD.SUCCESS:
        dialogs.alert(t('Your password has been successfully reset. You can now login with your new password.'), {
          title: t('Password Reset Successful'),
          okText: t('OK'),
          onClose: () => {
            navigate('/login');
          },
        });
        break;
    }
  }, [authResetPasswordInfo, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = useCallback((data) => {
    if (!passwordStrengthCriteria.every((test) => test(data.password || ''))) {
      dialogs.alert(t('Password does not meet strength requirements. Please check the checklist and try again.'), {
        title: t('Weak Password'),
        okText: t('OK'),
      });
      return;
    }

    if (data.password !== data.confirmPassword) {
      dialogs.alert(t('Passwords do not match. Please try again.'), {
        title: t('Password Mismatch'),
        okText: t('OK')
      });
      return;
    }

    dispatch(authResetPassword({
      payload: {
        email: data.email,
        token: data.token,
        password: data.password,
      }
    }));
  }, [dispatch, dialogs, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const onError = useCallback(() => {
    dialogs.alert(t('Please fill in all required fields correctly.'), {
      title: t('Invalid Input'),
      okText: t('OK')
    });
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit, onError)}
      gap={4}
      sx={{
        backgroundColor: 'background.default',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        position: 'relative',
      }}
    >
      <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%', mt: -2, px: 2 }}>
        <LanguageSwitcher />
      </Stack>

      <Stack gap={2} sx={{ alignItems: 'center', display: 'flex', width: isMobile ? '90%' : '280px' }}>
        <img src={icon} alt='icon' style={{ width: '100px', height: '100px' }} />

        <Typography variant='h5' color='secondary.main' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {t('GIVING')}
        </Typography>
      </Stack>

      <Stack sx={{ width: isMobile ? '95%' : '500px', flexDirection: 'column', display: 'flex', alignItems: 'center' }}>
        <Typography variant='h2' color='secondary.main' sx={{ p: 2, fontWeight: 'bold' }}>
          {t('RESET PASSWORD')}
        </Typography>
        <Divider sx={{ width: '95%', height: '4px', backgroundColor: 'primary.main' }} />
      </Stack>

      <Stack gap={2} sx={{ alignItems: 'center', display: 'flex', width: isMobile ? '90%' : '500px' }}>
        <Typography variant='body1' sx={{ textAlign: 'center', px: 2 }}>
          {t('Enter your new password below.')}
        </Typography>

        {authResetPasswordInfo.error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {get(authResetPasswordInfo, 'error.error.description', t('Failed to reset password. Please try again.'))}
          </Alert>
        )}

        <TextInput
          label={t('Email')}
          type="email"
          value={watch('email')}
          onChange={(e) => setValue('email', e.target.value)}
          error={!!errors.email}
          helper={errors.email?.message}
          fullWidth
          readOnly
          sx={{ backgroundColor: 'action.disabledBackground' }}
        />

        <PasswordInput
          label={t('New Password')}
          value={watchedPassword}
          onChange={(e) => setValue('password', e.target.value)}
          error={!!errors.password}
          helper={errors.password?.message ? t(errors.password.message) : undefined}
          showChecklist
          fullWidth
          placeholder={t('Enter new password')}
        />

        <PasswordInput
          label={t('Confirm New Password')}
          value={watchedConfirmPassword}
          onChange={(e) => setValue('confirmPassword', e.target.value)}
          onBlur={() => {
            if (watchedPassword && watchedConfirmPassword && watchedPassword !== watchedConfirmPassword) {
              setValue('confirmPassword', watchedConfirmPassword, { shouldDirty: true, shouldTouch: true });
            }
          }}
          error={!!errors.confirmPassword || (watchedPassword && watchedConfirmPassword && watchedPassword !== watchedConfirmPassword)}
          helper={
            errors.confirmPassword?.message ||
            (watchedPassword && watchedConfirmPassword && watchedPassword !== watchedConfirmPassword
              ? t('Passwords do not match')
              : t('Re-enter your new password'))
          }
          fullWidth
          placeholder={t('Confirm New Password')}
        />
      </Stack>

      <Stack sx={{ display: 'flex', width: '300px' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          leading={faLock}
          fullWidth
          disabled={
            isFetching ||
            !watchedPassword ||
            !watchedConfirmPassword ||
            watchedPassword !== watchedConfirmPassword ||
            !isPasswordStrong
          }
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          {isFetching ? t('Resetting...') : t('Reset Password')}
        </Button>
      </Stack>

      <Typography variant='body2'>
        <a href={`/giving/login?locale=${locale}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
          {t('Back to Login')}
        </a>
      </Typography>
    </Stack >
  );
}

function ResetPasswordPage() {
  return (
    <Suspense fallback={
      <Stack
        sx={{
          backgroundColor: 'background.default',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <CircularProgress />
      </Stack>
    }>
      <ResetPasswordContent />
    </Suspense>
  );
}

export default ResetPasswordPage;
